import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/crm/CrmShell";
import { Btn, Field, inputCls, EmptyState, Modal, StatCard, fmtDay } from "@/components/crm/kit";
import { Building2, Plus, Layers } from "lucide-react";

export const CLIENT_STATUS_TONE = {
  "Not Started": "bg-slate-100 text-slate-600", Onboarding: "bg-sky-100 text-sky-700",
  "DTCM Form Pending": "bg-amber-100 text-amber-700", "DTCM Form Completed": "bg-indigo-100 text-indigo-700",
  "Ready to Start": "bg-violet-100 text-violet-700", "Started writing prescription": "bg-teal-100 text-teal-700",
  "Started with Caregenix for RPM": "bg-teal-100 text-teal-700", "Client is sending Infusion": "bg-teal-100 text-teal-700",
  Active: "bg-emerald-100 text-emerald-700", "On Hold": "bg-orange-100 text-orange-700", Inactive: "bg-red-100 text-red-700",
};
export const SERVICE_TONE = {
  "Not Started": "bg-slate-100 text-slate-500", Onboarding: "bg-amber-100 text-amber-700",
  Active: "bg-emerald-100 text-emerald-700", "On Hold": "bg-orange-100 text-orange-700",
  Completed: "bg-blue-100 text-blue-700", Cancelled: "bg-red-100 text-red-700",
};
export function ClientStatusPill({ status, testid }) {
  return <span data-testid={testid} className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${CLIENT_STATUS_TONE[status] || "bg-slate-100 text-slate-600"}`}>{status || "—"}</span>;
}

export default function Clients() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState({ client_statuses: [], services: [], users: [] });
  const [dash, setDash] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: sp.get("status") || "", service: "", owner_id: "" });
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", type: "", city: "", state: "", owner_id: "", client_status: "Not Started" });

  const load = useCallback(async () => {
    const params = { clients_only: true };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const r = await api.get("/crm/clients", { params });
    setRows(r.data);
  }, [filters]);

  useEffect(() => {
    api.get("/crm/hudlup/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/crm/hudlup/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const activeServices = dash ? Object.values(dash.active_by_service).reduce((a, b) => a + b, 0) : 0;

  const create = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    try {
      const r = await api.post("/crm/clients", form);
      toast.success("Client created");
      setShow(false); setForm({ name: "", type: "", city: "", state: "", owner_id: "", client_status: "Not Started" });
      nav(`/crm/clients/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Clients & Services" subtitle="Converted clients, onboarding status and active services">
        <Btn onClick={() => setShow(true)} data-testid="client-new"><Plus className="h-4 w-4" /> New Client</Btn>
      </PageHeader>

      <div className="p-6">
        {/* Metrics */}
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard testid="cl-stat-converted" label="Converted clients" value={dash?.converted_clients ?? "—"} />
          <StatCard testid="cl-stat-dtcm-pending" label="DTCM pending" value={dash?.dtcm_pending ?? "—"} />
          <StatCard testid="cl-stat-dtcm-complete" label="DTCM completed" value={dash?.dtcm_completed ?? "—"} />
          <StatCard testid="cl-stat-active-services" label="Active services" value={activeServices} />
          <StatCard testid="cl-stat-overdue" label="Overdue follow-ups" value={dash?.overdue_followups ?? "—"} sub="referral follow-ups" />
        </div>

        {/* Filters */}
        <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <input data-testid="cl-search" className={inputCls} placeholder="Search clients…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
          <select data-testid="cl-filter-status" className={inputCls} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All statuses</option>
            {meta.client_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select data-testid="cl-filter-service" className={inputCls} value={filters.service} onChange={(e) => setFilters({ ...filters, service: e.target.value })}>
            <option value="">All services</option>
            {meta.services.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
          <select data-testid="cl-filter-owner" className={inputCls} value={filters.owner_id} onChange={(e) => setFilters({ ...filters, owner_id: e.target.value })}>
            <option value="">All owners</option>
            {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>

        {/* List */}
        {rows.length === 0 ? (
          <EmptyState icon={Building2} title="No clients yet" hint="Create a client or convert a won opportunity to begin onboarding." />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Organization</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Client Status</th><th className="px-4 py-3">Services</th><th className="px-4 py-3">Last activity</th></tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => nav(`/crm/clients/${c.id}`)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3"><p className="font-semibold text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.type || "—"} · {c.city || "?"}{c.state ? `, ${c.state}` : ""}</p></td>
                    <td className="px-4 py-3 text-slate-600">{c.owner_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="px-4 py-3"><ClientStatusPill status={c.client_status} /></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.services || []).length === 0 && <span className="text-xs text-slate-400">—</span>}
                        {(c.services || []).slice(0, 3).map((s) => (
                          <span key={s.id} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SERVICE_TONE[s.status] || "bg-slate-100 text-slate-500"}`}>{s.service_name}</span>
                        ))}
                        {(c.services || []).length > 3 && <span className="text-[11px] text-slate-400">+{c.services.length - 3}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-400">{fmtDay(c.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dash && Object.keys(dash.active_by_service).length > 0 && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <p className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700"><Layers className="h-4 w-4 text-primary" /> Clients active by service</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dash.active_by_service).map(([svc, n]) => (
                <span key={svc} className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-semibold text-emerald-700">{svc}: {n}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <Modal open={show} onClose={() => setShow(false)} title="New Client">
        <Field label="Organization / practice name *"><input data-testid="client-name" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><input className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="MSO, Practice…" /></Field>
          <Field label="Client status">
            <select data-testid="client-status-new" className={inputCls} value={form.client_status} onChange={(e) => setForm({ ...form, client_status: e.target.value })}>
              {meta.client_statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="City"><input className={inputCls} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
          <Field label="State"><input className={inputCls} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></Field>
          <Field label="Owner">
            <select data-testid="client-owner-new" className={inputCls} value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
              <option value="">Unassigned</option>
              {meta.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
        </div>
        <Btn onClick={create} data-testid="client-save" className="mt-2 w-full justify-center">Create Client</Btn>
      </Modal>
    </div>
  );
}
